/**
 * Mudar a ORDEM dos materiais fixados no topo.
 *
 * ## O pedido (Gisele, 23/10/2026)
 *
 * "Permitir alterar a ordem de exibição dos vídeos fixados."
 *
 * Fixar já existia (ponto do 19/10): o fixado vai para o topo, e dentro do
 * grupo vale `order` e depois o título. O que não existia era mandar em
 * `order` -- a coluna nasce 0 em todas as linhas, então na prática os fixados
 * saíam em ordem alfabética, e quem fixa três vídeos quer o mais importante em
 * primeiro, não o que começa com "A".
 *
 * ## Por que um módulo, e não um `onClick` na tela
 *
 * A parte que pode errar em silêncio é a conta: trocar o item com o vizinho
 * ERRADO (o vizinho na lista inteira, e não o vizinho entre os fixados),
 * gravar a ordem de quem não mudou, ou gravar `order` em material não fixado
 * -- que o desfixaria de lugar sem ninguém pedir. Essas três têm teste aqui.
 *
 * ## A ordem é escrita para TODOS os fixados, não só para os dois que trocaram
 *
 * Porque todos nascem com 0: sem reescrever o grupo, trocar dois deixaria o
 * resto empatado em zero e o desempate voltaria a ser o título -- a lista
 * saltaria para uma ordem que ninguém pediu. Só são gravados os que de fato
 * mudam de valor, então a segunda troca seguida grava duas linhas, não dez.
 */

/** Os fixados, na ordem em que a tela os mostra. */
const apenasFixados = (materiais) => materiais.filter((m) => Boolean(m.fixado));

/**
 * Move um material fixado uma posição para cima ou para baixo.
 *
 * @param {object[]} materiais  a lista COMO A TELA MOSTRA (já ordenada)
 * @param {number|string} id    o material a mover
 * @param {'cima'|'baixo'} direcao
 * @returns {{lista: object[], gravar: {id: any, order: number}[]}}
 *   `lista` é a nova ordem para a tela; `gravar` é o que precisa ir à api.
 *   Nada muda (e `gravar` vem vazio) quando o movimento não é possível.
 */
export const reordenarFixados = (materiais, id, direcao) => {
    const lista = Array.isArray(materiais) ? materiais : [];
    const fixados = apenasFixados(lista);

    const atual = fixados.findIndex((m) => m.id === id);
    const destino = direcao === 'cima' ? atual - 1 : atual + 1;

    // Fora do grupo, ou já na ponta: nada a fazer. Devolver a lista original
    // (e não uma cópia reordenada igual) deixa a tela não re-renderizar à toa.
    if (atual === -1 || destino < 0 || destino >= fixados.length) {
        return { lista, gravar: [] };
    }

    const novaOrdem = [...fixados];
    [novaOrdem[atual], novaOrdem[destino]] = [novaOrdem[destino], novaOrdem[atual]];

    // A posição vira o `order`. Só entra em `gravar` quem mudou de valor.
    const gravar = [];
    const ordemPorId = new Map();

    novaOrdem.forEach((material, posicao) => {
        ordemPorId.set(material.id, posicao);
        if ((material.order ?? 0) !== posicao) {
            gravar.push({ id: material.id, order: posicao });
        }
    });

    /*
     * A lista devolvida mantém os NÃO fixados onde estavam. Reordenar a lista
     * inteira aqui seria reimplementar a regra de exibição num segundo lugar,
     * e os dois divergiriam no primeiro ajuste feito num só.
     */
    const fixadosNovos = novaOrdem.map((m) => ({ ...m, order: ordemPorId.get(m.id) }));
    let proximo = 0;

    const resultado = lista.map((material) => (
        material.fixado ? fixadosNovos[proximo++] : material
    ));

    return { lista: resultado, gravar };
};

/** O material pode subir? (É fixado e não é o primeiro do grupo.) */
export const podeSubir = (materiais, id) => apenasFixados(materiais || [])
    .findIndex((m) => m.id === id) > 0;

/** O material pode descer? (É fixado e não é o último do grupo.) */
export const podeDescer = (materiais, id) => {
    const fixados = apenasFixados(materiais || []);
    const posicao = fixados.findIndex((m) => m.id === id);

    return posicao !== -1 && posicao < fixados.length - 1;
};
